import {Injectable} from '@angular/core';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { MenuApiService } from './menu-api.service';
import type { MobileMenuItem } from './menu-api.service';

export interface MenuPermissionActions {
  [key: string]: boolean | null | undefined;
}

export interface SubSection {
  name: string;  // Cambiado de 'label' a 'name'
  route: string;
  icon?: string; // Agregar icon opcional
  permissionKey?: string | null;
  actions?: MenuPermissionActions;
  subSections?: SubSection[]; // Soporta submenús anidados (nivel 4)
}

export interface Section { // Third Level
  name: string;  // Cambiado de 'label' a 'name'
  route: string;
  icon?: string; // Agregar icon opcional
  permissionKey?: string | null;
  actions?: MenuPermissionActions;
  subSections?: SubSection[];
}

export interface MainOption { // First Level
  name: string;  // Cambiado de 'label' a 'name'
  route: string;
  icon?: string; // Agregar icon opcional
  type?: string | null;
  subOptions?: SubOption[]; // Agregar subOptions
}

export interface SubOption { // Second Level
  name: string;  // Cambiado de 'label' a 'name'
  route: string;
  icon?: string; // Agregar icon opcional
  showLevel?: boolean;
  permissionKey?: string | null;
  actions?: MenuPermissionActions;
  sections?: Section[];
}

export interface UserMenuResponse {
  defaultMainRoute: string;
  defaultSubRoute: string;
  defaultSectionRoute: string;
  defaultMenu?: MainOption | null;
  mainOptions: MainOption[];
}

export interface ResolvedMenuRouteContext {
  main?: MainOption;
  sub?: SubOption;
  section?: Section;
  subSection?: SubSection;
  permissionKey: string | null;
  actions: MenuPermissionActions;
}

interface InternalMenuNodeMatch {
  section: Section;
  subSection?: SubSection;
  permissionKey: string | null;
  actions: MenuPermissionActions;
  depth: number;
  start: number;
  span: number;
}

@Injectable({providedIn: 'root'})
export class MenuService {
  private menuData$ = new BehaviorSubject<UserMenuResponse | null>(null);
  private isLoading$ = new BehaviorSubject<boolean>(false);
  private mobileRouteContexts = new Map<string, {
    permissionKey: string | null;
    actions: MenuPermissionActions;
  }>();

  constructor(private menuApiService: MenuApiService) {}

  // Cargar menú desde el backend
  loadUserMenu(): Observable<UserMenuResponse> {
    this.isLoading$.next(true);
    return this.menuApiService.getUserMenu().pipe(
      tap(menuData => {

        this.menuData$.next(menuData);
        this.isLoading$.next(false);
      }),
      catchError(error => {
        this.isLoading$.next(false);

        return throwError(error);
      })
    );
  }

  // Getters para acceder a los datos
  get menuData(): UserMenuResponse | null {
    return this.menuData$.value;
  }

  get mainOptions(): MainOption[] {
    return this.menuData?.mainOptions || [];
  }

  get defaultRoutes() {
    const data = this.menuData;
    const main = this.findMain(data?.defaultMainRoute || '')
      ?? this.findMain(data?.defaultMenu?.route || '')
      ?? this.mainOptions[0];
    const sub = this.findSub(main, data?.defaultSubRoute || '')
      ?? this.findSub(main, data?.defaultMenu?.subOptions?.[0]?.route || '')
      ?? this.getSubOptions(main)[0];
    const section = this.findSection(main, sub, data?.defaultSectionRoute || '')
      ?? this.findSection(main, sub, data?.defaultMenu?.subOptions?.[0]?.sections?.[0]?.route || '')
      ?? this.getSections(main, sub)[0];

    return {
      main: main?.route || data?.defaultMainRoute || data?.defaultMenu?.route || 'crm',
      sub: sub?.route || data?.defaultSubRoute || data?.defaultMenu?.subOptions?.[0]?.route || 'prospects',
      section: section?.route || data?.defaultSectionRoute || data?.defaultMenu?.subOptions?.[0]?.sections?.[0]?.route || 'tracking'
    };
  }

  get isLoading(): boolean {
    return this.isLoading$.value;
  }

  // Observables para reactividad
  getMenuData$(): Observable<UserMenuResponse | null> {
    return this.menuData$.asObservable();
  }

  getIsLoading$(): Observable<boolean> {
    return this.isLoading$.asObservable();
  }

  // Métodos de búsqueda (adaptados para usar datos dinámicos)
  getSubOptions(main: MainOption | undefined): SubOption[] {
    return main?.subOptions || [];
  }

  getSections(main: MainOption | undefined, sub: SubOption | undefined): Section[] {
    return sub?.sections || [];
  }

  getSubSections(section: Section | undefined): SubSection[] {
    return section?.subSections || [];
  }

  findMain(route: string): MainOption | undefined {
    return this.mainOptions.find(m => m.route === route);
  }

  findSub(main: MainOption | undefined, route: string): SubOption | undefined {
    if (!main) return undefined;
    return this.getSubOptions(main).find(s => s.route === route);
  }

  findSection(main: MainOption | undefined, sub: SubOption | undefined, route: string): Section | undefined {
    if (!main || !sub) return undefined;
    return this.getSections(main, sub).find(sec => sec.route === route);
  }

  findSubSection(section: Section | undefined, route: string): SubSection | undefined {
    if (!section) return undefined;
    return (section.subSections || []).find(ss => ss.route === route);
  }

  getRouteActions(
    mainRoute: string,
    subRoute?: string,
    sectionRoute?: string,
    subSectionRoute?: string
  ): MenuPermissionActions {
    const main = this.findMain(mainRoute);
    const sub = subRoute ? this.findSub(main, subRoute) : undefined;
    const section = sectionRoute ? this.findSection(main, sub, sectionRoute) : undefined;
    const subSection = subSectionRoute ? this.findSubSection(section, subSectionRoute) : undefined;

    if (subSection?.actions) {
      return subSection.actions;
    }
    if (section?.actions) {
      return section.actions;
    }
    if (sub?.actions) {
      return sub.actions;
    }
    return {};
  }

  getRoutePermissionKey(
    mainRoute: string,
    subRoute?: string,
    sectionRoute?: string,
    subSectionRoute?: string
  ): string | null {
    const main = this.findMain(mainRoute);
    const sub = subRoute ? this.findSub(main, subRoute) : undefined;
    const section = sectionRoute ? this.findSection(main, sub, sectionRoute) : undefined;
    const subSection = subSectionRoute ? this.findSubSection(section, subSectionRoute) : undefined;

    return subSection?.permissionKey
      ?? section?.permissionKey
      ?? sub?.permissionKey
      ?? null;
  }

  hasRouteAction(
    actionKey: string,
    mainRoute: string,
    subRoute?: string,
    sectionRoute?: string,
    subSectionRoute?: string
  ): boolean {
    return !!this.getRouteActions(mainRoute, subRoute, sectionRoute, subSectionRoute)?.[actionKey];
  }

  resolveRouteContext(url: string): ResolvedMenuRouteContext | null {
    const mobileContext = this.resolveMobileRouteContext(url);
    if (mobileContext) {
      return mobileContext;
    }

    const menuData = this.menuData;
    if (!menuData) {
      return null;
    }

    const segments = this.normalizeUrlSegments(url);
    if (!segments.length) {
      return null;
    }

    const [mainSeg, subSeg] = segments;
    const defaults = this.defaultRoutes;

    const main = this.findMain(mainSeg) ?? this.findMain(defaults.main);
    if (!main) {
      return null;
    }

    const sub = this.findSub(main, subSeg)
      ?? this.getSubOptions(main).find(option => this.routeAliases(option.route, [main.route]).includes(this.normalizeSegment(subSeg)))
      ?? this.getSubOptions(main)[0];

    if (!sub) {
      return {
        main,
        permissionKey: null,
        actions: {}
      };
    }

    const remainingSegments = segments.slice(2);
    const match = this.findBestMenuNodeMatch(main, sub, remainingSegments);

    return {
      main,
      sub,
      section: match?.section,
      subSection: match?.subSection,
      permissionKey: match?.permissionKey ?? sub.permissionKey ?? null,
      actions: match?.actions ?? sub.actions ?? {}
    };
  }

  getActionsForUrl(url: string): MenuPermissionActions {
    return this.resolveRouteContext(url)?.actions ?? {};
  }

  getPermissionKeyForUrl(url: string): string | null {
    return this.resolveRouteContext(url)?.permissionKey ?? null;
  }

  hasActionForUrl(url: string, actionKey: string): boolean {
    return !!this.getActionsForUrl(url)?.[actionKey];
  }

  setMobileMenuItems(items: MobileMenuItem[] | null | undefined): void {
    this.mobileRouteContexts.clear();

    (items ?? []).forEach(item => {
      const route = this.normalizeUrlPath(item.route);
      if (!route) {
        return;
      }

      this.mobileRouteContexts.set(route, {
        permissionKey: item.permissionKey ?? null,
        actions: item.actions ?? {}
      });
    });
  }

  filterRowActionsForUrl<T extends Record<string, boolean | null | undefined>>(
    actions: T | null | undefined,
    url: string
  ): Partial<T> {
    if (!actions || typeof actions !== 'object') {
      return {};
    }

    const routeActions = this.getActionsForUrl(url);
    const normalizedRouteActions = new Set(
      Object.keys(routeActions ?? {}).filter(key => !!routeActions[key]).map(key => this.normalizeSegment(key))
    );
    const filtered = {} as Partial<T>;

    Object.keys(actions).forEach((key) => {
      const aliases = this.actionAliases(key);
      const hasPermission = aliases.some(alias => normalizedRouteActions.has(this.normalizeSegment(alias)));
      filtered[key as keyof T] = (Boolean(actions[key]) && hasPermission) as T[keyof T];
    });

    return filtered;
  }

  private findBestMenuNodeMatch(
    main: MainOption,
    sub: SubOption,
    remainingSegments: string[]
  ): {
    section: Section;
    subSection?: SubSection;
    permissionKey: string | null;
    actions: MenuPermissionActions;
  } | null {
    if (!remainingSegments.length) {
      return null;
    }

    const normalizedSegments = remainingSegments.map(segment => this.normalizeSegment(segment));
    const candidates = this.buildRouteCandidates(main, sub);
    let bestMatch: InternalMenuNodeMatch | null = null;

    candidates.forEach(candidate => {
      const match = this.matchAliasChain(normalizedSegments, candidate.aliasChain);
      if (!match) {
        return;
      }

      if (
        !bestMatch
        || candidate.depth > bestMatch.depth
        || (candidate.depth === bestMatch.depth && match.span < bestMatch.span)
        || (candidate.depth === bestMatch.depth && match.span === bestMatch.span && match.start < bestMatch.start)
      ) {
        bestMatch = {
          section: candidate.section,
          subSection: candidate.subSection,
          permissionKey: candidate.permissionKey,
          actions: candidate.actions,
          depth: candidate.depth,
          start: match.start,
          span: match.span
        };
      }
    });

    const resolvedMatch = bestMatch as InternalMenuNodeMatch | null;
    if (!resolvedMatch) {
      return null;
    }

    return {
      section: resolvedMatch.section,
      subSection: resolvedMatch.subSection,
      permissionKey: resolvedMatch.permissionKey,
      actions: resolvedMatch.actions
    };
  }

  private buildRouteCandidates(main: MainOption, sub: SubOption): Array<{
    section: Section;
    subSection?: SubSection;
    permissionKey: string | null;
    actions: MenuPermissionActions;
    aliasChain: string[][];
    depth: number;
  }> {
    const candidates: Array<{
      section: Section;
      subSection?: SubSection;
      permissionKey: string | null;
      actions: MenuPermissionActions;
      aliasChain: string[][];
      depth: number;
    }> = [];

    const visitNode = (
      section: Section,
      node: Section | SubSection,
      chain: Array<Section | SubSection>,
      ancestorRoutes: string[]
    ) => {
      const nextChain = [...chain, node];
      const aliasChain = nextChain.map((part, index) => {
        const chainAncestors = [
          main.route,
          sub.route,
          ...nextChain.slice(0, index).map(item => item.route),
          ...ancestorRoutes
        ];

        return this.routeAliases(part.route, chainAncestors);
      });

      candidates.push({
        section,
        subSection: node === section ? undefined : node as SubSection,
        permissionKey: node.permissionKey ?? null,
        actions: node.actions ?? {},
        aliasChain,
        depth: nextChain.length
      });

      const childNodes = this.getNestedSubSections(node);
      childNodes.forEach(child => {
        visitNode(section, child, nextChain, ancestorRoutes);
      });
    };

    (sub.sections ?? []).forEach(section => {
      visitNode(section, section, [], []);
    });

    return candidates;
  }

  private getNestedSubSections(node: Section | SubSection): SubSection[] {
    if (!node || !('subSections' in node)) {
      return [];
    }

    return Array.isArray(node.subSections) ? node.subSections : [];
  }

  private matchAliasChain(
    remainingSegments: string[],
    aliasChain: string[][]
  ): { start: number; span: number } | null {
    let searchIndex = 0;
    let firstMatch = -1;
    let lastMatch = -1;

    for (const aliases of aliasChain) {
      let foundIndex = -1;

      for (let index = searchIndex; index < remainingSegments.length; index += 1) {
        if (aliases.includes(remainingSegments[index])) {
          foundIndex = index;
          break;
        }
      }

      if (foundIndex === -1) {
        return null;
      }

      if (firstMatch === -1) {
        firstMatch = foundIndex;
      }
      lastMatch = foundIndex;
      searchIndex = foundIndex + 1;
    }

    return {
      start: firstMatch,
      span: Math.max(0, lastMatch - firstMatch)
    };
  }

  private routeAliases(route: string, ancestors: string[]): string[] {
    const normalizedRoute = this.normalizeSegment(route);
    const aliases = new Set<string>([normalizedRoute]);
    const genericPrefixes = new Set([
      'crm',
      'panel',
      'dashboard',
      'prospects',
      'conversations',
      'campaigns',
      'inventory',
      'inventories',
      'consultants',
      'sales',
      'administration',
      'customer-service',
      'site',
      'real-estate',
      'users-administration'
    ]);

    const normalizedAncestors = ancestors
      .map(value => this.normalizeSegment(value))
      .filter(Boolean);

    normalizedAncestors.forEach(ancestor => {
      if (normalizedRoute.startsWith(`${ancestor}-`)) {
        aliases.add(normalizedRoute.slice(ancestor.length + 1));
      }

      // Support child routes that repeat the parent slug in Angular paths.
      if (!genericPrefixes.has(ancestor) && ancestor !== normalizedRoute && !normalizedRoute.startsWith(`${ancestor}-`)) {
        aliases.add(`${ancestor}-${normalizedRoute}`);
      }
    });

    genericPrefixes.forEach(prefix => {
      if (normalizedRoute.startsWith(`${prefix}-`)) {
        aliases.add(normalizedRoute.slice(prefix.length + 1));
      }
    });

    if (normalizedRoute.endsWith('-list')) {
      aliases.add(normalizedRoute.slice(0, -5));
    }

    if (normalizedRoute.endsWith('-data')) {
      aliases.add(normalizedRoute.slice(0, -5));
    }

    if (normalizedRoute === 'general-data') {
      aliases.add('general');
    }

    if (normalizedRoute.endsWith('-general-data')) {
      aliases.add('general');
    }

    Array.from(aliases).forEach(alias => {
      if (!alias) {
        return;
      }

      if (!alias.endsWith('s')) {
        aliases.add(`${alias}s`);
      } else if (alias.length > 1) {
        aliases.add(alias.slice(0, -1));
      }
    });

    return Array.from(aliases).filter(Boolean);
  }

  private actionAliases(actionKey: string): string[] {
    const aliases = new Set<string>([actionKey]);

    if (actionKey === 'blocked') {
      aliases.add('status');
    }
    if (actionKey === 'status') {
      aliases.add('blocked');
    }
    if (actionKey === 'depositRequest') {
      aliases.add('reservation');
    }
    if (actionKey === 'reservation') {
      aliases.add('depositRequest');
    }

    return Array.from(aliases);
  }

  private normalizeUrlSegments(url: string): string[] {
    return (url || '')
      .split('?')[0]
      .split('#')[0]
      .split('/')
      .filter(Boolean)
      .map(segment => this.normalizeSegment(segment));
  }

  private normalizeUrlPath(url: string | null | undefined): string {
    const segments = this.normalizeUrlSegments(url || '');
    return segments.length ? `/${segments.join('/')}` : '';
  }

  private resolveMobileRouteContext(url: string): ResolvedMenuRouteContext | null {
    const path = this.normalizeUrlPath(url);
    if (!path || !this.mobileRouteContexts.size) {
      return null;
    }

    let match: {
      route: string;
      permissionKey: string | null;
      actions: MenuPermissionActions;
    } | null = null;

    for (const [route, context] of this.mobileRouteContexts.entries()) {
      const matchesRoute = path === route || path.startsWith(`${route}/`);
      if (!matchesRoute) {
        continue;
      }

      if (!match || route.length > match.route.length) {
        match = {
          route,
          permissionKey: context.permissionKey,
          actions: context.actions
        };
      }
    }

    if (!match) {
      return null;
    }

    return {
      permissionKey: match.permissionKey,
      actions: match.actions
    };
  }

  private normalizeSegment(value: string | null | undefined): string {
    return String(value ?? '')
      .trim()
      .toLowerCase();
  }


}
